import { FC } from "react";

import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MuiDrawer from "@mui/material/Drawer";
import { Theme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import Logo from "../Logo";
import NavigationList from "./components/NavigationList";

import { brandColorTokens } from "@constants";
import { appLayoutDimensions } from "../../AppLayout.constants";

import { IDrawerProperties } from "./Drawer.types";

const { headerHeight, drawerWidth, drawerCollapsedWidth } = appLayoutDimensions;

const paperStyle = {
  width: drawerWidth,
  bgcolor: brandColorTokens.background.inverted,
  color: brandColorTokens.text.bodyInverted,
};

const Drawer: FC<IDrawerProperties> = ({ isDesktop, isOpen, onClose }) => {
  const { t } = useTranslation();

  if (isDesktop) {
    const desktopWidth = isOpen ? drawerWidth : drawerCollapsedWidth;
    const widthTransition = (theme: Theme) => theme.transitions.create("width");

    return (
      <MuiDrawer
        variant="permanent"
        data-cy="app-layout-drawer"
        sx={{
          position: "sticky",
          top: headerHeight,
          alignSelf: "flex-start",
          width: desktopWidth,
          height: `calc(100dvh - ${headerHeight}px)`,
          flexShrink: 0,
          transition: widthTransition,
        }}
        slotProps={{
          paper: {
            sx: {
              ...paperStyle,
              position: "absolute",
              height: "100%",
              width: desktopWidth,
              overflowX: "hidden",
              transition: widthTransition,
            },
          },
        }}
        data-cy-collapsed={!isOpen}
      >
        <NavigationList isCollapsed={!isOpen} />
      </MuiDrawer>
    );
  }

  return (
    <MuiDrawer
      variant="temporary"
      open={isOpen}
      onClose={onClose}
      data-cy="app-layout-drawer"
      slotProps={{ paper: { sx: { ...paperStyle, maxWidth: "85vw" } } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: headerHeight,
          flexShrink: 0,
          pl: 3,
          pr: 1,
          borderBottom: 1,
          borderColor: brandColorTokens.line.light,
        }}
      >
        <Logo isInverted={true} onNavigate={onClose} />
        <IconButton color="inherit" aria-label={t("drawerV2.close")} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <NavigationList isCollapsed={false} onNavigate={onClose} />
    </MuiDrawer>
  );
};

export default Drawer;
